import React from 'react';
import { Box, Tooltip, Chip } from '@mui/material';
import { CaretUpOutlined, CaretDownOutlined } from '@ant-design/icons';
import { theme, ThemedTypography, createTableCell } from './index';
import { createFilterDropdown, FILTER_CONFIG } from '../../components/TechnicalAlertsTable/filterConfig';
import { formatArray } from '../../components/TechnicalAlertsTable/DataTransformationLayer';
import { getAlertTypeText, formatUsualWeeklyUnits } from '../../utils/alertBadgeHelpers';
import AlertTypeBadge from '../../components/shared/AlertTypeBadge';
import { openAlertSalesPanel } from '../../components/shared/AlertSalesPanel';
import { prefetchRecentSales } from '../../hooks/useRecentSales';

// Resting on a link starts loading the alert's sales, so the panel usually opens from cache.
// The short delay skips links the pointer only crosses on its way elsewhere.
const HOVER_PREFETCH_DELAY_MS = 120;
let hoverPrefetchTimer = null;

const prefetchAlertSales = (record, clientId, productId) => {
  clearTimeout(hoverPrefetchTimer);
  hoverPrefetchTimer = setTimeout(() => {
    prefetchRecentSales(clientId, record.d_store_id, productId).catch(() => {});
  }, HOVER_PREFETCH_DELAY_MS);
};

const cancelAlertSalesPrefetch = () => clearTimeout(hoverPrefetchTimer);

/**
 * Centralized column width configuration
 * Following the pattern used in ExperimentsTable
 */
export const COLUMN_WIDTHS = {
  selection: 15, // Selection column - matching experiments table
  sector: 60, // Sector column
  group: 120,
  location: 100, 
  machineId: 60,
  route: 66,
  productName: 100,
  alertType: 100,
  slots: 55,
  weeksSince: 85,
  totalLost: 140,
  actions: 100
};

/**
 * Centralized sort icon renderer
 * Provides consistent visual feedback for sortable columns
 */
export const renderSortIcon = ({ sortOrder }) => {
  if (sortOrder === 'ascend') {
    return <CaretUpOutlined style={{ color: theme.colors.primary }} />;
  }
  if (sortOrder === 'descend') {
    return <CaretDownOutlined style={{ color: theme.colors.primary }} />;
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', opacity: 0.3 }}>
      <CaretUpOutlined style={{ fontSize: '10px', lineHeight: 1 }} />
      <CaretDownOutlined style={{ fontSize: '10px', lineHeight: 1, marginTop: '-2px' }} />
    </Box>
  );
};

/**
 * Column renderer factories
 * These create reusable column renderers with consistent styling
 */
export const columnRenderers = {
  // Basic text column with themed styling
  text: (text) => createTableCell(text),
  
  // Weeks since with localized count, and what the product usually sold there - t function passed as parameter
  weeksSince: (weeks, t, usualWeeklyUnits) => (
    <Box>
      <ThemedTypography.Body3>
        {t('weekCount', { count: weeks })}
      </ThemedTypography.Body3>
      {usualWeeklyUnits > 0 && (
        <ThemedTypography.Caption>
          {t('alertUsualWeeklySales', { units: formatUsualWeeklyUnits(usualWeeklyUnits) })}
        </ThemedTypography.Caption>
      )}
    </Box>
  ),
  
  // Revenue with alert styling and proper formatting
  revenue: (revenueLoss) => (
    <ThemedTypography.Body3 sx={{ 
      fontWeight: 600,
      color: theme.colors.alert
    }}>
      €{revenueLoss ? revenueLoss.toLocaleString('en-US', { 
        minimumFractionDigits: 0, 
        maximumFractionDigits: 0 
      }) : '0'}
    </ThemedTypography.Body3>
  ),
  
  // Clickable machine ID that opens the whole machine's latest sales in the right sidebar
  clickableMachineId: (machineId, record, navigate, clientId, t) => {
    const handleMachineIdClick = () => openAlertSalesPanel(record, navigate, clientId, 'machine');

    return (
      <Tooltip title={`${record.group || ''} - ${record.location || ''} · ${t('alerts_viewRecentSales')}`} arrow placement="top">
        <ThemedTypography.Clickable
          component="span"
          onClick={handleMachineIdClick}
          onMouseEnter={() => prefetchAlertSales(record, clientId, null)}
          onMouseLeave={cancelAlertSalesPrefetch}
          sx={{
            color: theme.colors.text.muted,
            '&:hover': {
              color: theme.colors.text.secondary
            }
          }}
        >
          {machineId || '-'}
        </ThemedTypography.Clickable>
      </Tooltip>
    );
  },
  
  // Product name that opens the alert's latest sales in the right sidebar
  clickableProductName: (productName, record, navigate, clientId, t) => (!productName ? createTableCell(productName) : (
    <Tooltip title={t('alerts_viewRecentSales')} arrow placement="top">
      <ThemedTypography.Clickable
        component="span"
        onClick={() => openAlertSalesPanel(record, navigate, clientId)}
        onMouseEnter={() => prefetchAlertSales(record, clientId, record.d_product_id)}
        onMouseLeave={cancelAlertSalesPrefetch}
        sx={{
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          '&:hover': {
            color: theme.colors.primary,
            textDecoration: 'underline'
          }
        }}
      >
        {productName}
      </ThemedTypography.Clickable>
    </Tooltip>
  )),

  // Slots array formatter
  slots: (slots) => {
    return createTableCell(formatArray(slots, 5, ', '));
  },
  
  // Route renderer with tooltip and graceful fallback
  routes: (routes, record) => {
    const rawRoutes = Array.isArray(routes) && routes.length > 0
      ? routes
      : Array.isArray(record?.routes) && record.routes.length > 0
        ? record.routes
        : record?.route
          ? [record.route]
          : Array.isArray(record?.store?.routes) && record.store.routes.length > 0
            ? record.store.routes
            : record?.store?.route
              ? [record.store.route]
              : undefined;

    let names = [];

    if (Array.isArray(rawRoutes)) {
      names = rawRoutes
        .map(route => {
          if (route && typeof route === 'object') {
            return route.name || route.code || route.label || route.description || route.id;
          }
          return route;
        })
        .filter(Boolean)
        .map(name => String(name));
    } else if (rawRoutes) {
      const single = rawRoutes.name || rawRoutes.code || rawRoutes.label || rawRoutes;
      names = single ? [String(single)] : [];
    }

    if (names.length === 0) {
      return createTableCell('-');
    }

    const displayText = names.slice(0, 2).join(', ');
    const tooltipText = names.join(', ');
    const hasOverflow = names.length > 2;

    return (
      <Tooltip title={tooltipText} arrow placement="top">
        <ThemedTypography.Body3 sx={{
          color: theme.colors.text.primary,
          whiteSpace: 'nowrap'
        }}>
          {hasOverflow ? `${displayText}…` : displayText}
        </ThemedTypography.Body3>
      </Tooltip>
    );
  },
  
  // Alert type with pill badge
  alertType: (type, record, t) => {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <AlertTypeBadge alertType={type} t={t} />
      </Box>
    );
  },
  
  // Status with human-readable text for filtering
  status: (status, record, t) => {
    const getStatusText = (status, t) => {
      switch (status) {
        case 'resolved':
          return t ? t('resolved') : 'Resolved';
        case 'under_review':
          return t ? t('underReview') : 'Under Review';
        case 'not_apply':
          return t ? t('notBroken') : 'Working';
        case 'pending':
        default:
          return t ? t('pending') : 'Pending';
      }
    };
    return createTableCell(getStatusText(status, t));
  },
  
  // Actions column with state-based chips - t function passed as parameter
  actions: (_, record, openPopover, t) => {
    
    // State 1: Pending review - match card view styling
    if (record.status === 'pending' || (record.technical_problem === null && record.status !== 'under_review')) {
      return (
        <Box sx={{
          display: 'flex',
          gap: '4px',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <Tooltip title={t('clickToReview')}>
            <Chip
              label={t('review')}
              size="small"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                openPopover(e, 'review', { recordId: record.id });
              }}
              sx={{
                cursor: 'pointer',
                fontSize: '10px',
                height: '20px',
                backgroundColor: 'rgba(142, 142, 147, 0.05)', // Apple gray background like card
                borderColor: 'rgba(142, 142, 147, 0.2)', // Apple gray border like card
                color: '#8E8E93', // Apple gray text like card
                fontWeight: 500,
                '&:hover': { 
                  backgroundColor: 'rgba(142, 142, 147, 0.08)',
                  borderColor: 'rgba(142, 142, 147, 0.3)'
                },
                '& .MuiChip-label': {
                  px: '6px'
                }
              }}
            />
          </Tooltip>
        </Box>
      );
    }

    // State 2: Under review (broken) — closes by itself when the product sells again.
    // Clicking reopens the review so it can still be marked as working.
    if (record.status === 'under_review' || (record.technical_problem === true && !record.resolution_date)) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Tooltip title={t('underReviewHint')}>
            <Chip
              label={t('underReview')}
              size="small"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                openPopover(e, 'review', { recordId: record.id });
              }}
              sx={{
                cursor: 'pointer',
                fontSize: '10px',
                height: '20px',
                backgroundColor: 'rgba(255, 149, 0, 0.05)', // Apple orange background like card
                borderColor: 'rgba(255, 149, 0, 0.2)', // Apple orange border like card
                color: '#FF9500', // Apple orange text like card
                fontWeight: 500,
                '&:hover': { 
                  backgroundColor: 'rgba(255, 149, 0, 0.08)',
                  borderColor: 'rgba(255, 149, 0, 0.3)'
                },
                '& .MuiChip-label': {
                  px: '6px'
                }
              }}
            />
          </Tooltip>
        </Box>
      );
    }

    return null;
  }
};

/**
 * Base column configuration factory
 * Creates standardized column objects with consistent patterns
 */
export const createBaseColumn = ({
  key,
  titleKey,
  dataIndex = key,
  width = COLUMN_WIDTHS[key],
  sortable = false,
  align = 'left',
  ellipsis = false,
  t,
  ...customProps
}) => {
  const baseColumn = {
    title: t(titleKey || key),
    key,
    dataIndex,
    width,
    align,
    ellipsis,
    ...customProps
  };
  
  // Add sorting support
  if (sortable) {
    baseColumn.sorter = true;
    baseColumn.sortIcon = renderSortIcon;
  }
  
  return baseColumn;
};

/**
 * Technical Alerts specific column definitions
 * Pre-configured columns ready to use with proper state integration
 */
export const technicalAlertsColumns = {
  sector: (state, filterValues, filtersLoading, t, onFilterChange) => ({
    ...createBaseColumn({
      key: 'sector',
      titleKey: 'sector',
      sortable: true,
      ellipsis: true,
      t
    }),
    sortOrder: state.table.sortedInfo.columnKey === 'sector' ? state.table.sortedInfo.order : null,
    render: columnRenderers.text,
    filterDropdown: createFilterDropdown('sector', filterValues, FILTER_CONFIG.sector.translationKey, filtersLoading, t, onFilterChange),
    filteredValue: state.table.filteredInfo.sector || null
  }),
  group: (state, filterValues, filtersLoading, t, onFilterChange) => ({
    ...createBaseColumn({
      key: 'group',
      titleKey: 'group', 
      sortable: true,
      ellipsis: true,
      t
    }),
    sortOrder: state.table.sortedInfo.columnKey === 'group' ? state.table.sortedInfo.order : null,
    render: columnRenderers.text,
    filterDropdown: createFilterDropdown('group', filterValues, FILTER_CONFIG.group.translationKey, filtersLoading, t, onFilterChange),
    filteredValue: state.table.filteredInfo.group || null
  }),
  
  route: (state, filterValues, filtersLoading, t, onFilterChange) => ({
    ...createBaseColumn({
      key: 'route',
      titleKey: 'route',
      dataIndex: 'routes',
      ellipsis: true,
      t
    }),
    render: (routes, record) => columnRenderers.routes(routes, record),
    filterDropdown: createFilterDropdown('route', filterValues, FILTER_CONFIG.route.translationKey, filtersLoading, t, onFilterChange),
    filteredValue: state.table.filteredInfo.route || null
  }),
  
  location: (state, filterValues, filtersLoading, t) => ({
    ...createBaseColumn({
      key: 'location',
      titleKey: 'location',
      sortable: true,
      ellipsis: true,
      t
    }),
    sortOrder: state.table.sortedInfo.columnKey === 'location' ? state.table.sortedInfo.order : null,
    render: columnRenderers.text,
    filterDropdown: createFilterDropdown('location', filterValues, FILTER_CONFIG.location.translationKey, filtersLoading, t),
    filteredValue: state.table.filteredInfo.location || null
  }),
  
  machineId: (state, filterValues, filtersLoading, t, navigate, clientId) => ({
    ...createBaseColumn({
      key: 'machineId',
      titleKey: 'machineId',
      dataIndex: 'machine_id',
      sortable: true,
      t
    }),
    sortOrder: state.table.sortedInfo.columnKey === 'machineId' ? state.table.sortedInfo.order : null,
    render: (machineId, record) => columnRenderers.clickableMachineId(machineId, record, navigate, clientId, t),
    filterDropdown: createFilterDropdown('machineId', filterValues, FILTER_CONFIG.machineId.translationKey, filtersLoading, t),
    filteredValue: state.table.filteredInfo.machineId || null
  }),
  
  productName: (state, filterValues, filtersLoading, t, navigate, clientId) => ({
    ...createBaseColumn({
      key: 'productName',
      titleKey: 'productName',
      dataIndex: 'product_name',
      sortable: true,
      ellipsis: true,
      t
    }),
    sortOrder: state.table.sortedInfo.columnKey === 'productName' ? state.table.sortedInfo.order : null,
    render: (productName, record) => columnRenderers.clickableProductName(productName, record, navigate, clientId, t),
    filterDropdown: createFilterDropdown('productName', filterValues, FILTER_CONFIG.productName.translationKey, filtersLoading, t),
    filteredValue: state.table.filteredInfo.productName || null
  }),
  
  slots: (state, filterValues, filtersLoading, t) => ({
    ...createBaseColumn({
      key: 'slots',
      titleKey: 'slotsColumn',
      dataIndex: 'selection_code', // Use backend field name
      align: 'center',
      t
    }),
    render: columnRenderers.slots
    // No filtering capability for slots column - backend doesn't support it
  }),
  
  alertType: (state, filterValues, filtersLoading, t, handleImmediateFilterChange) => ({
    ...createBaseColumn({
      key: 'alertType',
      titleKey: 'alertType',
      dataIndex: 'alert_type',
      sortable: true,
      ellipsis: true,
      t
    }),
    sortOrder: state.table.sortedInfo.columnKey === 'alertType' ? state.table.sortedInfo.order : null,
    render: (type, record) => columnRenderers.alertType(type, record, t),
    filterDropdown: createFilterDropdown('alertType', filterValues, FILTER_CONFIG.alertType.translationKey, filtersLoading, t, handleImmediateFilterChange),
    filteredValue: state.table.filteredInfo.alertType || null
  }),
  
  weeksSince: (state, filterValues, filtersLoading, t) => ({
    ...createBaseColumn({
      key: 'weeksSince',
      titleKey: 'weeksSince',
      dataIndex: 'weeks_since',
      align: 'center',
      sortable: true,
      t
    }),
    sortOrder: state.table.sortedInfo.columnKey === 'weeksSince' ? state.table.sortedInfo.order : null,
    render: (weeks, record) => columnRenderers.weeksSince(weeks, t, Number(record.estimated_loss_weekly_units)),
    filterDropdown: createFilterDropdown('weeksSince', filterValues, FILTER_CONFIG.weeksSince.translationKey, filtersLoading, t),
    filteredValue: state.table.filteredInfo.weeksSince || null
  }),
  
  totalLost: (state, filterValues, filtersLoading, t) => ({
    ...createBaseColumn({
      key: 'totalLost',
      titleKey: 'totalLost',
      dataIndex: 'revenue_loss', // Backend field name (already correct)
      align: 'center',
      sortable: true,
      t
    }),
    sortOrder: state.table.sortedInfo.columnKey === 'totalLost' ? state.table.sortedInfo.order : null,
    render: columnRenderers.revenue
  }),
  
  actions: (state, filterValues, filtersLoading, t, openPopover, handleImmediateFilterChange) => ({
    ...createBaseColumn({
      key: 'actions',
      titleKey: 'actions',
      align: 'center',
      sortable: true,
      t
    }),
    sortOrder: state.table.sortedInfo.columnKey === 'actions' ? state.table.sortedInfo.order : null,
    render: (_, record) => columnRenderers.actions(_, record, openPopover, t),
    filterDropdown: createFilterDropdown('status', filterValues, FILTER_CONFIG.status.translationKey, filtersLoading, t, handleImmediateFilterChange),
    filteredValue: state.table.filteredInfo.actions || null
  })
};

/**
 * Column configuration factory function
 * Creates complete column configuration for Technical Alerts table
 */
export const createTechnicalAlertsColumns = (state, filterValues, filtersLoading, navigate, openPopover, t, clientId) => {
  // Create immediate filter change handler
  const handleImmediateFilterChange = (newFilters) => {
    // Merge with existing filters
    const mergedFilters = {
      ...state.table.filteredInfo,
      ...newFilters
    };
    
    // Remove null values
    Object.keys(mergedFilters).forEach(key => {
      if (mergedFilters[key] === null || (Array.isArray(mergedFilters[key]) && mergedFilters[key].length === 0)) {
        delete mergedFilters[key];
      }
    });
    
    // Call the table change handler with the new filters
    state.table.handleTableChange(null, mergedFilters, state.table.sortedInfo);
  };
  
  return [
    technicalAlertsColumns.sector(state, filterValues, filtersLoading, t, handleImmediateFilterChange),
    technicalAlertsColumns.route(state, filterValues, filtersLoading, t, handleImmediateFilterChange),
    technicalAlertsColumns.group(state, filterValues, filtersLoading, t, handleImmediateFilterChange),
    technicalAlertsColumns.location(state, filterValues, filtersLoading, t, handleImmediateFilterChange),
    technicalAlertsColumns.machineId(state, filterValues, filtersLoading, t, navigate, clientId),
    technicalAlertsColumns.productName(state, filterValues, filtersLoading, t, navigate, clientId),
    technicalAlertsColumns.alertType(state, filterValues, filtersLoading, t, handleImmediateFilterChange),
    technicalAlertsColumns.slots(state, filterValues, filtersLoading, t), // No filter change handler for slots
    technicalAlertsColumns.weeksSince(state, filterValues, filtersLoading, t, handleImmediateFilterChange),
    technicalAlertsColumns.totalLost(state, filterValues, filtersLoading, t),
    technicalAlertsColumns.actions(state, filterValues, filtersLoading, t, openPopover, handleImmediateFilterChange)
  ];
};
